import React, { FC, useEffect, useRef, useState, memo, useCallback, useMemo } from 'react';

import { CheckSquareOutlined, CheckSquareTwoTone } from '@ant-design/icons';
import { Button, Checkbox, message, Tooltip } from 'antd';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';

import ToggleFilter from './ToggleFilter';
import { IFilter } from '../../../../../../app/providers/redux/slices/reports/params/filter/types';
import { ITableDataGeneric } from '../../../../../../app/providers/redux/slices/reports/table/types';
import {
    findObjectByLabelId,
    getGroupTitle,
    isFirstGrouping,
    sortDataRecursive,
    sortDataTextRecursive
} from '../../../../../../helpers/utils/functions';
import { generateListKeys, prepareData, buildDisplayGroupings, formatMembershipSynced, CUSTOM_GROUP_SET_KEY, resolveGroupAxisFilter } from '../../../../../../helpers/utils/reports';
import { showInfoModal } from '../../../../../../helpers/utils/ui';
import { injectUtmSectionsIntoFilters } from '../../../../../../helpers/utils/utmSections';
import { data as dataFilters, resolveFilterIdByGrouping, updateFilters } from '../../../../constants/filters';
import { useActions } from '../../../../hooks/useActions';
import useHover from '../../../../hooks/useHover';
import { useTypedSelector } from '../../../../hooks/useTypedSelector';
import { useUtmSections } from '../../../../hooks/useUtmSections';
import { TextWithTooltip } from '../../../ui/textWithTooltip/TextWithTooltip';

// ==================== Типы ====================

interface GroupCheckedItem {
    ids: string[];
    pathStr: string;
    label: string;
}

// ==================== Компонент ====================

const GroupCell: FC<{
    rowData: ITableDataGeneric;
    expandedRowKeys?: string[];
    onCellWidthChange: (width: number) => void;
    columnWidth?: number;
    hideTotalCheckbox?: boolean;
    /** Скрыть чекбоксы выбора строк группировок */
    hideRowCheckboxes?: boolean;
    /** Принудительно разрешить множественный выбор (игнорировать groupsLimit из Redux) */
    forceMultiSelect?: boolean;
    /** ID блока дашборда (для чтения данных из блочного state) */
    blockId?: string;
    /** Открыт в модалке дашборда (скоуп фильтров — dashboardModal) */
    isModal?: boolean;
}> = ({ rowData, expandedRowKeys, onCellWidthChange, columnWidth, hideTotalCheckbox = false, hideRowCheckboxes = false, forceMultiSelect = false, blockId, isModal = false }) => {
    const { hovered,bind } = useHover();
    const { groupsChecked, totalChecked, groupsLimit: reduxGroupsLimit,groupings,tableData: { data }, sortTable, tableData:{ totalChosen } } = useTypedSelector((state) =>
        blockId ? (state.table.blocks[blockId] ?? state.table) : state.table
    );
    // Фильтр по группам регистраций того же скоупа, что и groupings: приводит ось
    // к фильтру (уровень-группа не по фильтру выкидывается) — иначе серая подпись
    // и таймстемп группового уровня легли бы на не-групповые строки.
    const scopedFilters = useTypedSelector((state) =>
        blockId
            ? (state.filter.blocks[blockId]?.filters ?? [])
            : isModal
                ? (state.filter.dashboardModal?.filters ?? [])
                : state.filter.filters
    );
    const groupFilter = useMemo(() => resolveGroupAxisFilter(scopedFilters), [ scopedFilters ]);

    // Уровни для отображения: набор «Групп регистраций» схлопнут в один синтетический
    // уровень «Группы регистраций» — чтобы серая подпись уровня совпадала с глубиной строки
    // (иначе индекс попадал бы на первую группу и показывал её имя).
    const displayGroupings = useMemo(() => buildDisplayGroupings(groupings, groupFilter), [ groupings, groupFilter ]);

    // «Группы регистраций» — глобальные (не блочные). Карты группа → membership_synced_at:
    // по dataKey (custom_group_<id>) и по имени — чтобы у строки-сегмента группы показать
    // компактную метку последней пересборки.
    const customGroups = useTypedSelector((state) => state.table.customGroups);
    // Карты группа → membership_synced_at по нескольким ключам (id, dataKey, имя):
    // в строке-сегменте идентификатор группы может прийти по-разному, а имя-сегмент
    // часто не совпадает с именем группы («Дата ютм» ≠ «Дата + ютм»).
    const { groupSyncedById, groupSyncedByKey, groupSyncedByName } = useMemo(() => {
        const byId = new Map<string, string | null>();
        const byKey = new Map<string, string | null>();
        const byName = new Map<string, string | null>();
        const children = customGroups?.[0]?.children ?? [];
        children.forEach((group: any) => {
            const synced = group?.membership_synced_at ?? null;
            if (group?.id != null) byId.set(String(group.id), synced);
            if (group?.dataKey) byKey.set(String(group.dataKey), synced);
            if (group?.name != null) byName.set(String(group.name).trim(), synced);
        });
        return { groupSyncedById: byId, groupSyncedByKey: byKey, groupSyncedByName: byName };
    }, [ customGroups ]);
    // Метка пересборки для ТЕКУЩЕЙ строки:
    //  - сольная группа (уровень custom_group_<id>) — вся ось = одна группа, ищем по dataKey уровня;
    //  - свёрнутый набор (custom_group_set) — строка = отдельная группа, идентификатор строки
    //    может прийти как id/ключ/имя, поэтому пробуем все три источника.
    const rowSynced = useMemo(() => {
        const path = (rowData.clientData as { path?: string[] } | undefined)?.path;
        const level = displayGroupings[(path?.length ?? 0) - 1] as { dataKey?: string } | undefined;
        const levelKey = level?.dataKey;
        const hasKey = (map: Map<string, string | null>, k?: string) =>
            k != null && k !== '' && map.has(k);
        let synced: string | null | undefined;
        if (typeof levelKey === 'string' && levelKey.startsWith('custom_group_') && levelKey !== CUSTOM_GROUP_SET_KEY) {
            synced = groupSyncedByKey.get(levelKey);
        } else if (levelKey === CUSTOM_GROUP_SET_KEY) {
            const rowId = rowData.id != null ? String(rowData.id) : '';
            const cleanedName = typeof rowData.group === 'string'
                ? rowData.group.replace(/<[^>]*>/g, '').trim()
                : '';
            if (hasKey(groupSyncedById, rowId)) synced = groupSyncedById.get(rowId);
            else if (hasKey(groupSyncedByKey, rowId)) synced = groupSyncedByKey.get(rowId);
            else if (hasKey(groupSyncedByName, cleanedName)) synced = groupSyncedByName.get(cleanedName);
            else return null;
        } else {
            return null;
        }
        return formatMembershipSynced(synced);
    }, [ rowData.id, rowData.clientData, rowData.group, displayGroupings, groupSyncedById, groupSyncedByKey, groupSyncedByName ]);
    // Группа регистраций текущей строки (если уровень — «Группы регистраций»):
    //  - сольный уровень custom_group_<id> — вся ось = одна группа, id зашит в ключ уровня;
    //  - свёрнутый набор custom_group_set — строка = отдельная группа, id строки может прийти
    //    как rowData.id либо только по имени, поэтому пробуем оба источника через customGroups.
    // Нужна, чтобы у строки-группы показать плюсик «добавить в фильтр» (app_custom_groups),
    // подставив ЧИСЛОВОЙ id группы (его читает resolveGroupAxisFilter из checkeds[].id).
    const rowGroup = useMemo<{ id: number; name: string } | null>(() => {
        const path = (rowData.clientData as { path?: string[] } | undefined)?.path;
        const level = displayGroupings[(path?.length ?? 0) - 1] as { dataKey?: string } | undefined;
        const levelKey = level?.dataKey;
        if (typeof levelKey !== 'string' || !levelKey.startsWith('custom_group_')) return null;
        const children: any[] = customGroups?.[0]?.children ?? [];
        const cleanedName = typeof rowData.group === 'string'
            ? rowData.group.replace(/<[^>]*>/g, '').trim()
            : String(rowData.group ?? '');
        // Сольный уровень-группа: id зашит в ключ уровня
        const soloMatch = levelKey !== CUSTOM_GROUP_SET_KEY ? levelKey.match(/^custom_group_(\d+)$/) : null;
        if (soloMatch) {
            const id = Number(soloMatch[1]);
            const grp = children.find((group) => Number(group?.id) === id);
            return { id, name: grp?.name ?? cleanedName };
        }
        // Свёрнутый набор: строка = отдельная группа (id строки либо по имени)
        if (levelKey === CUSTOM_GROUP_SET_KEY) {
            const byId = rowData.id != null
                ? children.find((group) => String(group?.id) === String(rowData.id))
                : undefined;
            const grp = byId ?? children.find((group) => String(group?.name).trim() === cleanedName);
            if (!grp || grp.id == null) return null;
            return { id: Number(grp.id), name: grp.name ?? cleanedName };
        }
        return null;
    }, [ rowData.id, rowData.group, rowData.clientData, displayGroupings, customGroups ]);
    // Если forceMultiSelect включён, игнорируем ограничение из Redux
    const groupsLimit = forceMultiSelect ? false : reduxGroupsLimit;
    // Фильтры того же скоупа, куда и пишем: блок → blocks[blockId], модалка
    // дашборда → dashboardModal, обычный отчёт → глобальные. Иначе плюсик писал бы
    // в глобальный список, а модалка/блок его не видели (тост есть, фильтра нет).
    const { filters } = useTypedSelector((state) =>
        blockId
            ? (state.filter.blocks[blockId] ?? { filters: [] })
            : isModal
                ? state.filter.dashboardModal
                : state.filter
    );
    const { setTotalChecked, clearTotalChecked, setGroupingsChecked, setMetaChartFetching, setMetaCompareChartFetching,setTotalChosenStatus,setFilter,setFilters,setMetaFilterUpdate } = useActions();
    // Стабилизируем ссылки на action creators через ref,
    // т.к. useActions() возвращает новые ссылки на каждый рендер
    const actionsRef = useRef({ setGroupingsChecked, setTotalChecked, clearTotalChecked, setMetaChartFetching, setMetaCompareChartFetching, setTotalChosenStatus });
    actionsRef.current = { setGroupingsChecked, setTotalChecked, clearTotalChecked, setMetaChartFetching, setMetaCompareChartFetching, setTotalChosenStatus };
    const [ firstGrouping,setFirstGrouping ] = useState(data?.length > 0 ? data[0] : undefined);
    const cellRef = useRef<HTMLDivElement>(null);
    const [ groupTitlePos,setGroupTitlePos ] = useState({
        left:'0px',
    });
    const isNested = rowData.children && (rowData.children.length > 1 || rowData.children[0].group !== 'empty');
    const spanRef = useRef(null);
    const [ dataFilter,setDataFilter ] = useState(dataFilters);
    // Пункты разобранных UTM-меток в статическом дереве отсутствуют — достраиваем,
    // иначе плюсик «добавить в фильтр» не найдёт фильтр для строки отчёта
    const utmSections = useUtmSections();
    const dataFilterWithUtm = useMemo(
        () => injectUtmSectionsIntoFilters(dataFilter, utmSections),
        [ dataFilter, utmSections ]
    );

    useEffect(()=>{
        if(location.pathname === '/users/ad-reports'){
            setDataFilter(updateFilters());
        }
    },[ location.pathname ]);
    useEffect(() => {
        const wrapperDiv = cellRef.current;
        if (wrapperDiv && wrapperDiv.offsetLeft) {
            setGroupTitlePos({ left: `${isNested ? wrapperDiv.offsetLeft - 22 - 4 : wrapperDiv.offsetLeft}px` });
        }
    }, [cellRef, rowData, isNested]);

    useEffect(() => {
        if (Object.values(sortTable).length > 0) {
            const sortedData = sortDataTextRecursive(data, sortTable);
            setFirstGrouping(sortedData[0]);
        } else {
            setFirstGrouping(data?.length > 0 ? data[0] : undefined);
        }
    }, [ sortTable ]);


    const onChange = useCallback((e: CheckboxChangeEvent) => {
        e.stopPropagation();
        const actions = actionsRef.current;
        // Используем groupsChecked напрямую из замыкания, а не через ref
        // Это гарантирует актуальное значение на момент вызова
        const currentGroupsChecked = groupsChecked;

        // В режиме forceMultiSelect (тип "Таблица" в модальном окне дашборда) 
        // НЕ вызываем setMetaChartFetching/setMetaCompareChartFetching,
        // т.к. графика нет, а эти вызовы провоцируют бесконечный цикл обновлений в UniversalFilter
        const triggerChartFetching = () => {
            if (!forceMultiSelect) {
                actions.setMetaChartFetching({ value:true });
                actions.setMetaCompareChartFetching({ value:true });
            }
        };

        if (groupsLimit) {
            if (rowData.pathStr === 'total') {
                if (e.target.checked) {
                    actions.setTotalChecked();
                    actions.setGroupingsChecked([]);
                } else {
                    actions.clearTotalChecked();
                }

                triggerChartFetching();
                return;
            }
            if (rowData.pathStr === 'total_choosen'){
                if(e.target.checked){
                    if(currentGroupsChecked.length > 0){
                        actions.setTotalChosenStatus([ rowData ]);
                        triggerChartFetching();
                    }else{
                        showInfoModal('Нет выбранных.');
                    }
                }else{
                    actions.setTotalChosenStatus([]);
                }
                return;
            }
            if (e.target.checked) {
                const clientData = rowData.clientData as { path: string[]; pathLabel?: string[] } | undefined;
                const ids = clientData?.path || [];
                const pathStr = rowData.pathStr || '';
                const label = clientData?.pathLabel?.join(' → ') || '';
                actions.clearTotalChecked();
                actions.setGroupingsChecked([{ ids, pathStr, label }]);

                triggerChartFetching();
            } else {
                actions.setGroupingsChecked(currentGroupsChecked.filter((item) => item.pathStr !== rowData.pathStr));

                triggerChartFetching();
            }

            return;
        }

        if (rowData.pathStr === 'total') {
            if (e.target.checked) {
                actions.setTotalChecked();
            } else {
                actions.clearTotalChecked();
                actions.setGroupingsChecked(currentGroupsChecked.filter((item) => item.pathStr !== rowData.pathStr));
            }

            triggerChartFetching();
            return;
        }
        if (rowData.pathStr === 'total_choosen'){
            if(e.target.checked){
                if(currentGroupsChecked.length > 0){
                    actions.setTotalChosenStatus([ rowData ]);
                    triggerChartFetching();
                }else{
                    showInfoModal('Нет выбранных.');
                }
            }else{
                actions.setTotalChosenStatus([]);
            }
            return;
        }

        if (e.target.checked) {
            const clientData2 = rowData.clientData as { path: string[]; pathLabel?: string[] } | undefined;
            const ids = clientData2?.path || [];
            const pathStr = rowData.pathStr || '';
            const label = clientData2?.pathLabel?.join(' → ') || '';
            actions.setGroupingsChecked([...currentGroupsChecked, { ids, pathStr, label }]);

            triggerChartFetching();
        } else {
            actions.setGroupingsChecked(currentGroupsChecked.filter((item) => item.pathStr !== rowData.pathStr));

            triggerChartFetching();
        }
    }, [rowData, groupsLimit, forceMultiSelect, groupsChecked]);

    const findChildrenChecked = useCallback((groups: ITableDataGeneric[] | undefined): boolean => {
        if (!groups) return false;

        let _l = 0;

        groups.forEach((item) => {
            if (groupsChecked.find((item2) => item2.pathStr === item.pathStr)) {
                _l += 1;
            }
        });

        return groups.length === _l;
    }, [groupsChecked]);

    const checkChildrenGroup = useCallback((groups: ITableDataGeneric[], isChecked: boolean) => {
        const actions = actionsRef.current;
        // Используем groupsChecked напрямую из замыкания
        const currentGroupsChecked = groupsChecked;
        const _gr: GroupCheckedItem[] = [];

        groups.forEach((item) => {
            if (item.clientData) {
                const ids = item.clientData.path;
                const pathStr = item.pathStr || '';
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const label = (item.clientData as any).pathLabel?.join(' → ') || '';
                _gr.push({ ids, pathStr, label });
            }
        });

        if (isChecked) {
            actions.setGroupingsChecked(currentGroupsChecked.filter((item) => {
                const _temp = _gr.find((grItem) => grItem.pathStr === item.pathStr);
                return !_temp;
            }));
        } else {
            actions.setGroupingsChecked([...currentGroupsChecked, ..._gr]);
        }

        if (!forceMultiSelect) {
            actions.setMetaChartFetching({ value: true });
            actions.setMetaCompareChartFetching({ value: true });
        }
    }, [forceMultiSelect, groupsChecked]);
    const checked = !![ ...groupsChecked, ...totalChecked,...totalChosen.active ].find((item) => item.pathStr === rowData.pathStr);
    const childrenChecked = findChildrenChecked(rowData.children);

    const renderFirstChildSpan = useCallback((row: ITableDataGeneric): boolean => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const clientData = row.clientData as any;
        if (clientData?.path && clientData.path.length > 1) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const findDataArr = Object.values(sortTable).length > 0 ? sortDataRecursive(data, sortTable) : data as any[];
            const parent = findObjectByLabelId(
                findDataArr,
                clientData.path[clientData.path.length - 2],
                clientData.pathLabel?.[clientData.path.length - 2] || ''
            );
            if (row.id === parent?.children?.[0]?.id) {
                return true;
            }
        }
        return false;
    }, [data, sortTable]);
    // Вычисление максимальной ширины с использованием ResizeObserver вместо перебора DOM
    const calculateMaxWidth = useCallback(() => {
        if (!spanRef.current) return 0;
        const element = spanRef.current as HTMLElement;
        return element.getBoundingClientRect().width + 60; // padding для checkbox и кнопок
    }, []);

    // Ref для groupsChecked, чтобы использовать в useEffect без зависимости
    const groupsCheckedRef = useRef(groupsChecked);
    groupsCheckedRef.current = groupsChecked;

    // Восстановление groupsChecked из URL-параметров при монтировании/смене строки
    useEffect(() => {
        const url = new URL(window.location.href);
        const currentParams = url.searchParams.get('params');
        const paramsObject = currentParams ? JSON.parse(currentParams) : {};
        const clientData3 = rowData.clientData as { path: string[]; pathLabel?: string[] } | undefined;
        const currentGroupsChecked = groupsCheckedRef.current;
        paramsObject?.groupsChecked?.forEach((el: string) => {
            if (!currentGroupsChecked.some((group: { pathStr: string }) => group.pathStr === el) && el === rowData.pathStr) {
                const ids = clientData3?.path || [];
                const pathStr = rowData.pathStr || '';
                const label = clientData3?.pathLabel?.join(' → ') || '';
                actionsRef.current.setGroupingsChecked([...currentGroupsChecked, { ids, pathStr, label }]);
            }
        });
    }, [rowData.pathStr]);

    // Пересчёт ширины столбца — только при изменении текста строки, НЕ при каждом ре-рендере.
    // Это предотвращает цикл: checkbox toggle → новый rowData → onCellWidthChange → columnWidth↑ → maxWidth↑ → width↑
    useEffect(() => {
        if (spanRef.current && rowData.pathStr !== 'total' && rowData.pathStr !== 'total_choosen') {
            const maxCheckboxWidth = calculateMaxWidth();
            if (maxCheckboxWidth > 0) {
                onCellWidthChange(maxCheckboxWidth);
            }
        }
    }, [rowData.group]); // eslint-disable-line

    const getFilterId = () => {
        // Строка-группа → фильтр «Группа регистраций» (в groupingsToFilters custom_group_* нет)
        if (rowGroup) return 'app_custom_groups';
        const clientData4 = rowData.clientData as { path: string[]; pathLabel?: string[] } | undefined;
        const groupingsKey = groupings[clientData4?.pathLabel?.length ? clientData4.pathLabel.length - 1 : 0]?.dataKey;
        return resolveFilterIdByGrouping(groupingsKey);
    };
    const isAlreadyExists = () => {
        let result = false;
        try{
            filters.forEach((el)=>{
                if(result){
                    return;
                }
                if(Object.prototype.hasOwnProperty.call(el, 'text')){
                    const isExistedFilter = filters.find((el)=>el.text === rowData.group.replace(/<a[^>]*>([^<]+)<\/a>/i, '$1'));
                    if(isExistedFilter){
                        result = true;
                    }
                }
                if(Object.prototype.hasOwnProperty.call(el, 'number')){
                    const rowValue = typeof rowData.group === 'number' ? rowData.group : +rowData.group.replace(/<a[^>]*>([^<]+)<\/a>/i, '$1');
                    const isExistedFilter = filters.find((el)=>el.number === rowValue);
                    if(isExistedFilter){
                        result = true;
                    }
                }
                if(Object.prototype.hasOwnProperty.call(el, 'items')) {
                    const filterId = getFilterId();
                    const filtersCloned = filters.map((el)=>el);
                    const isExistedFilter = filtersCloned.find((el) => el.id === filterId);
                    if (isExistedFilter) {
                        const indexToRemove = isExistedFilter?.items?.findIndex((el: { name: string }) => el.name === rowData.group);
                        if (indexToRemove !== -1) {
                            result = true;
                        }
                    }
                }
            });
        }catch(err){
            console.error(err);
        }

        return result;
    };
const successModal = (position:{top:string,left:string}) => {
    message.open({
        content: 'Фильтр добавлен',
        duration:1,
        type: 'success',
        style: { position:'fixed',top:position.top,left:position.left }
    });
};
    const handleGroupClick = (event: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
        const position = event.currentTarget.getBoundingClientRect();
        let filter:IFilter = {};
        const filterId = getFilterId();
        const __data = prepareData(dataFilterWithUtm, '', [], false,'','','','',[ '' ],'');
        const _data =  generateListKeys(__data);
        const findFilter = _data.find((el: { id: string; type?: string }) => el.id === filterId);
       if(findFilter){
           switch(findFilter.type){
               case 'text':{
                   const isExistedFilter = filters.find((el)=>el.text === rowData.group.replace(/<a[^>]*>([^<]+)<\/a>/i, '$1'));
                   if(isExistedFilter){
                       break;
                   }
                   filter = { ...findFilter,text:rowData.group.replace(/<a[^>]*>([^<]+)<\/a>/i, '$1') };
                   setFilter({ ...filter, isModal, blockId });
                   successModal({ top:`${position.top}px`,left:`${position.left}px` });
                   setMetaFilterUpdate({ value:true, blockId });
                   break;
               }
               case 'number':{
                   const rowValue = typeof rowData.group === 'number' ? rowData.group : +rowData.group.replace(/<a[^>]*>([^<]+)<\/a>/i, '$1');
                   const isExistedFilter = filters.find((el)=>el.number === rowValue);
                   if(isExistedFilter){
                       break;
                   }
                   filter = { ...findFilter,number:rowValue };
                   setFilter({ ...filter, isModal, blockId });
                   successModal({ top:`${position.top}px`,left:`${position.left}px` });
                   setMetaFilterUpdate({ value:true, blockId });
                   break;
               }
               default: {
                   // Для фильтра «Группа регистраций» бэкенд ожидает числовой id группы,
                   // а не rowData.id/имя — берём его из resolveRowGroup (rowGroup).
                   const isGroupFilter = findFilter.id === 'app_custom_groups' || findFilter.id === 'not_app_custom_groups';
                   // Очищаем HTML-ссылки из значения группировки
                   const cleanedGroup = isGroupFilter && rowGroup
                       ? rowGroup.name
                       : (typeof rowData.group === 'string'
                           ? rowData.group.replace(/<a[^>]*>([^<]+)<\/a>/i, '$1')
                           : rowData.group);
                   const filtersCloned = filters.map((el)=>el);
                   const isExistedFilter = filtersCloned.find((el)=>el.id === findFilter.id);
                   // Для фильтра user_email бэкенд ожидает email как id,
                   // а rowData.id — числовой ID пользователя
                   const useTextAsId = findFilter.id === 'user_email' || findFilter.id === 'not_user_email';
                   const itemId = isGroupFilter && rowGroup
                       ? rowGroup.id
                       : (useTextAsId ? cleanedGroup : rowData.id);
                   if(isExistedFilter){
                       const indexToRemove = isExistedFilter.items.findIndex((el:{name:string})=>el.name === cleanedGroup);
                       if(indexToRemove !== -1){
                           isExistedFilter.items.splice(indexToRemove,1);
                           isExistedFilter.checkeds.splice(indexToRemove,1);
                       }
                       filter = {
                           ...isExistedFilter,
                           items: [ ...isExistedFilter.items,{ id: itemId, name: cleanedGroup } ],
                           checkeds: [ ...isExistedFilter.checkeds,{ id: itemId, name: cleanedGroup } ]
                       };
                       const modifiedArray = JSON.parse(JSON.stringify(filters)) as IFilter[];
                       const index = modifiedArray.findIndex(el => el.id === filter.id);
                       if (index !== -1) {
                           modifiedArray.splice(index, 1);
                       }
                       setFilters({ data: [ ...modifiedArray, filter ], isModal, blockId });
                       successModal({ top:`${position.top}px`,left:`${position.left}px` });
                       setMetaFilterUpdate({ value:true, blockId });

                   }else{
                       filter = {
                           ...findFilter,
                           items: [ { id: itemId, name: cleanedGroup } ],
                           checkeds: [ { id: itemId, name: cleanedGroup } ]
                       };
                       setFilter({ ...filter, isModal, blockId });
                       successModal({ top:`${position.top}px`,left:`${position.left}px` });
                       setMetaFilterUpdate({ value:true, blockId });
                   }
                   break;
               }
           }

       }

    };
    const notToShow = [ 'total','total_choosen' ];
    const isSpecialRow = rowData.pathStr === 'total' || rowData.pathStr === 'total_choosen';
    return (
        <div className={'checkBoxWrapper'} ref={cellRef} {...(!isSpecialRow ? bind : {})}>
            {
                isNested ?
                    <Tooltip
                        placement="top"
                        title={<span style={{ fontSize: 11, whiteSpace: 'nowrap', color: '#fff' }}>Выбор всех значений</span>}
                        color="#333"
                        zIndex={9999}
                        trigger="hover"
                        mouseEnterDelay={2}
                        overlayInnerStyle={{ padding: '2px 6px', minHeight: 'auto', lineHeight: 1.2 }}
                    >
                        <Button
                            icon={childrenChecked ? <CheckSquareTwoTone /> : <CheckSquareOutlined />}
                            size="small"
                            type="link"
                            style={{ marginRight: 5, color: 'inherit' }}
                            onClick={() => checkChildrenGroup(rowData.children, childrenChecked)}
                        />
                    </Tooltip>
                    :
                    null
            }
            {rowData.pathStr === 'total_choosen' ? (
                <span ref={spanRef} className="checkbox-text" style={hideRowCheckboxes ? undefined : { marginLeft: 24 }}>
                    <span dangerouslySetInnerHTML={{ __html: rowData.group }} />
                </span>
            ) : rowData.pathStr === 'total' ? (
                hideTotalCheckbox ? (
                    <span ref={spanRef} className="checkbox-text" style={hideRowCheckboxes ? undefined : { marginLeft: 24 }}>
                        <span dangerouslySetInnerHTML={{ __html: rowData.group }} />
                    </span>
                ) : (
                    <div style={{ marginLeft: 3 }}>
                        <Checkbox checked={checked} onChange={onChange}>
                            <span ref={spanRef} className="checkbox-text">
                                <span dangerouslySetInnerHTML={{ __html: rowData.group }} />
                            </span>
                        </Checkbox>
                    </div>
                )
            ) : hideRowCheckboxes ? (
                <span ref={spanRef} className="checkbox-text">
                    <TextWithTooltip text={rowData.group} maxWidth={columnWidth ? columnWidth - 60 : undefined}/>
                </span>
            ) : (
                <Checkbox checked={checked} onChange={onChange}>
                    <span ref={spanRef} className="checkbox-text">
                        <TextWithTooltip text={rowData.group} maxWidth={columnWidth ? columnWidth - 60 : undefined}/>
                    </span>
                </Checkbox>
            )}
            {rowSynced && (
                <Tooltip title={`Связь группы пересобрана: ${rowSynced.full}`} placement="top">
                    <span style={{ marginLeft: 6, color: '#bbb', fontSize: 11, whiteSpace: 'nowrap' }}>
                        ⟳ {rowSynced.short}
                    </span>
                </Tooltip>
            )}
            {hovered && !notToShow.includes(rowData.pathStr || '') && getFilterId() && !isAlreadyExists() && <ToggleFilter handleGroupClick={handleGroupClick} />}
            {
                /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
                (renderFirstChildSpan(rowData) || isFirstGrouping(firstGrouping as any, firstGrouping as any, rowData as any)) && 
                    <span className="group-title-label" style={groupTitlePos}>
                        {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                        <TextWithTooltip text={getGroupTitle(rowData as any, displayGroupings)} maxWidth={columnWidth ? columnWidth - 40 : undefined}/>
                    </span> 
            }
        </div>
    );
};

export default memo(GroupCell);
